import React, { useState, useEffect } from 'react';
import api, { toArr } from '../api';

const C = {
  primary: '#6366f1', secondary: '#10b981', accent: '#f59e0b',
  card: 'rgba(255,255,255,0.05)', cardBorder: 'rgba(99,102,241,0.15)',
  text: '#fff', textMuted: 'rgba(255,255,255,0.5)', textSub: 'rgba(255,255,255,0.7)',
};

const defaultGoals = [
  { id: 1, title: 'Complete GenAI Certification', progress: 72, dueDate: 'Mar 31, 2025', priority: 'High', category: 'Certification' },
  { id: 2, title: 'Study 2 hours daily', progress: 58, dueDate: 'Ongoing', priority: 'Medium', category: 'Habit' },
  { id: 3, title: 'Pass Cloud Architecture Mock Test', progress: 40, dueDate: 'Feb 28, 2025', priority: 'High', category: 'Assessment' },
  { id: 4, title: 'Complete DevOps Module', progress: 30, dueDate: 'Apr 15, 2025', priority: 'Low', category: 'Course' },
];

const priorityColors = { High: '#ef4444', Medium: '#f59e0b', Low: '#10b981' };

const ElitureGoals = () => {
  const [goals, setGoals] = useState(defaultGoals);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newGoal, setNewGoal] = useState({ title: '', dueDate: '', priority: 'Medium', category: 'Course' });
  const [editingProgress, setEditingProgress] = useState(null); // goalId being updated
  const [progressInput, setProgressInput] = useState('');
  const [msg, setMsg] = useState({ type: '', text: '' });

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 3000); };

  useEffect(() => {
    api.get('/goals')
      .then(res => { const list = toArr(res.data); if (list.length) setGoals(list); })
      .catch(() => {}); // fall back to demo goals
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newGoal.title.trim()) return;
    const goalData = { ...newGoal, progress: 0 };
    try {
      const res = await api.post('/goals', goalData);
      setGoals(prev => [...prev, res.data]);
      showMsg('success', 'Goal added!');
    } catch {
      // API unavailable — add locally
      setGoals(prev => [...prev, { ...goalData, id: Date.now() }]);
      showMsg('success', 'Goal added locally.');
    }
    setNewGoal({ title: '', dueDate: '', priority: 'Medium', category: 'Course' });
    setShowAddForm(false);
  };

  const handleUpdateProgress = async (goal) => {
    const value = Math.min(100, Math.max(0, Number(progressInput)));
    const updated = { ...goal, progress: value };
    try {
      await api.put(`/goals/${goal.id}`, { progress: value });
    } catch {
      // update locally if API fails
    }
    setGoals(prev => prev.map(g => g.id === goal.id ? updated : g));
    setEditingProgress(null);
    setProgressInput('');
  };

  const handleDelete = async (goalId) => {
    try {
      await api.delete(`/goals/${goalId}`);
    } catch {
      // remove locally regardless
    }
    setGoals(prev => prev.filter(g => g.id !== goalId));
  };

  const iS = { width: '100%', background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10,
    padding: '10px 14px', color: '#fff', fontSize: '0.9rem',
    outline: 'none', boxSizing: 'border-box' };

  const completedCount = goals.filter(g => g.progress >= 100).length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <div style={{ fontSize: '1.3rem', fontWeight: 800, color: C.text }}>Learning Goals</div>
          <div style={{ fontSize: '0.8rem', color: C.textMuted, marginTop: 4 }}>
            {goals.length} goals · {completedCount} completed
          </div>
        </div>
        <button onClick={() => setShowAddForm(v => !v)} style={{
          background: 'linear-gradient(135deg, #6366f1, #10b981)',
          border: 'none', borderRadius: 10, padding: '10px 20px',
          color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem',
          boxShadow: '0 4px 15px rgba(99,102,241,0.3)',
        }}>
          {showAddForm ? '✕ Cancel' : '+ Add Goal'}
        </button>
      </div>

      {msg.text && (
        <div style={{ background: msg.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
          border: `1px solid ${msg.type === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
          borderRadius: 10, padding: '10px 16px', color: msg.type === 'success' ? C.secondary : '#ef4444',
          fontWeight: 600, fontSize: '0.875rem', marginBottom: 20 }}>
          {msg.type === 'success' ? '✅' : '⚠️'} {msg.text}
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`,
          borderRadius: 16, padding: 24, marginBottom: 24 }}>
          <div style={{ fontWeight: 700, color: C.text, marginBottom: 18, fontSize: '0.95rem' }}>New Goal</div>
          <form onSubmit={handleAdd} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: '0.75rem', color: C.textMuted, textTransform: 'uppercase',
                letterSpacing: 1, display: 'block', marginBottom: 6 }}>Goal Title</label>
              <input value={newGoal.title} onChange={e => setNewGoal(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Complete AWS Certification" required style={iS} />
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: C.textMuted, textTransform: 'uppercase',
                letterSpacing: 1, display: 'block', marginBottom: 6 }}>Due Date</label>
              <input type="date" value={newGoal.dueDate} onChange={e => setNewGoal(p => ({ ...p, dueDate: e.target.value }))}
                style={{ ...iS, colorScheme: 'dark' }} />
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: C.textMuted, textTransform: 'uppercase',
                letterSpacing: 1, display: 'block', marginBottom: 6 }}>Priority</label>
              <select value={newGoal.priority} onChange={e => setNewGoal(p => ({ ...p, priority: e.target.value }))}
                style={{ ...iS, background: 'rgba(30,27,60,0.9)' }}>
                <option>High</option><option>Medium</option><option>Low</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: C.textMuted, textTransform: 'uppercase',
                letterSpacing: 1, display: 'block', marginBottom: 6 }}>Category</label>
              <select value={newGoal.category} onChange={e => setNewGoal(p => ({ ...p, category: e.target.value }))}
                style={{ ...iS, background: 'rgba(30,27,60,0.9)' }}>
                <option>Course</option><option>Certification</option><option>Assessment</option><option>Habit</option>
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
              <button type="submit" style={{ background: 'linear-gradient(135deg, #6366f1, #10b981)',
                border: 'none', borderRadius: 10, padding: '10px 24px',
                color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Add Goal</button>
            </div>
          </form>
        </div>
      )}

      {/* Goals List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {goals.map((goal, i) => {
          const isComplete = (goal.progress || 0) >= 100;
          return (
            <div key={goal.id || i} style={{
              background: C.card, border: `1px solid ${isComplete ? 'rgba(16,185,129,0.3)' : C.cardBorder}`,
              borderRadius: 14, padding: '20px 24px',
              display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap',
              transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = isComplete ? 'rgba(16,185,129,0.3)' : C.cardBorder; }}
            >
              {/* Progress ring */}
              <div style={{ flexShrink: 0, position: 'relative', width: 60, height: 60 }}>
                <svg width="60" height="60" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx="30" cy="30" r="24" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
                  <circle cx="30" cy="30" r="24" fill="none"
                    stroke={isComplete ? C.secondary : C.primary} strokeWidth="4"
                    strokeDasharray={`${((goal.progress || 0) / 100) * 150.8} 150.8`}
                    strokeLinecap="round" />
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: '#fff', fontSize: '0.75rem', fontWeight: 700 }}>
                  {goal.progress || 0}%
                </div>
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, color: C.text, fontSize: '0.95rem' }}>
                    {goal.title || goal}
                  </span>
                  {goal.priority && (
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, color: priorityColors[goal.priority],
                      background: `${priorityColors[goal.priority]}20`, padding: '2px 8px', borderRadius: 20,
                      textTransform: 'uppercase', letterSpacing: 0.5 }}>{goal.priority}</span>
                  )}
                  {isComplete && (
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, color: C.secondary,
                      background: 'rgba(16,185,129,0.15)', padding: '2px 8px', borderRadius: 20 }}>✓ Done</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 16, fontSize: '0.75rem', color: C.textMuted, flexWrap: 'wrap' }}>
                  {goal.category && <span>📁 {goal.category}</span>}
                  {goal.dueDate && <span>📅 {goal.dueDate}</span>}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                {editingProgress === goal.id ? (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input type="number" value={progressInput}
                      onChange={e => setProgressInput(e.target.value)}
                      placeholder="0–100" min="0" max="100"
                      style={{ width: 70, background: 'rgba(255,255,255,0.07)',
                        border: '1px solid rgba(99,102,241,0.3)', borderRadius: 8,
                        padding: '6px 10px', color: '#fff', fontSize: '0.8rem', outline: 'none' }} />
                    <button onClick={() => handleUpdateProgress(goal)} style={{
                      background: 'linear-gradient(135deg, #6366f1, #10b981)',
                      border: 'none', borderRadius: 8, padding: '6px 12px',
                      color: '#fff', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                    }}>Save</button>
                    <button onClick={() => setEditingProgress(null)} style={{
                      background: 'rgba(255,255,255,0.06)', border: 'none',
                      borderRadius: 8, padding: '6px 10px', color: C.textMuted,
                      fontSize: '0.75rem', cursor: 'pointer',
                    }}>✕</button>
                  </div>
                ) : (
                  <>
                    <button onClick={() => { setEditingProgress(goal.id); setProgressInput(String(goal.progress || 0)); }}
                      style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)',
                        borderRadius: 8, padding: '6px 14px', color: C.primary, fontSize: '0.8rem',
                        fontWeight: 600, cursor: 'pointer' }}>
                      Update
                    </button>
                    <button onClick={() => handleDelete(goal.id)} style={{
                      background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                      borderRadius: 8, padding: '6px 10px', color: '#ef4444',
                      fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                    }}>✕</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {goals.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>◎</div>
          <div style={{ color: C.textMuted }}>No goals yet. Add your first goal above!</div>
        </div>
      )}
    </div>
  );
};

export default ElitureGoals;
